import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { useForm } from 'react-hook-form';
import axios from 'axios';

const DEFAULT_FORM_ENDPOINT = '/api/send-consult.php';
const DEFAULT_RECAPTCHA_ACTION = 'consult_form';

const getRuntimeConfig = () => {
  if (typeof window === 'undefined' || !window.RUNTIME_CONFIG) {
    return {};
  }

  return window.RUNTIME_CONFIG;
};

const loadRecaptchaScript = (siteKey) => {
  if (!siteKey) {
    return Promise.reject(
      new Error('No se encontro el site key de reCAPTCHA.')
    );
  }

  if (typeof window !== 'undefined' && window.grecaptcha) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const scriptId = 'google-recaptcha-v3-script';
    const existingScript = document.getElementById(scriptId);

    if (existingScript) {
      existingScript.addEventListener('load', () => resolve());
      existingScript.addEventListener('error', () => {
        reject(new Error('No se pudo cargar reCAPTCHA.'));
      });
      return;
    }

    const script = document.createElement('script');
    script.id = scriptId;
    script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('No se pudo cargar reCAPTCHA.'));
    document.head.appendChild(script);
  });
};

const getRecaptchaToken = async (
  siteKey,
  action = DEFAULT_RECAPTCHA_ACTION
) => {
  await loadRecaptchaScript(siteKey);

  if (!window.grecaptcha) {
    throw new Error('reCAPTCHA no esta disponible.');
  }

  await new Promise((resolve) => window.grecaptcha.ready(resolve));

  return window.grecaptcha.execute(siteKey, { action });
};

const breakpoints = {
  mobile: '768px',
  tablet: '1024px',
  desktop: '1440px',
};

const FormContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
`;

const FormContent = styled.form`
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
`;

const FormRow = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 5.375rem;

  @media (max-width: ${breakpoints.mobile}) {
    flex-direction: column;
    gap: 0.75rem;
    align-items: flex-start;
  }

  &:nth-child(4) {
    align-items: flex-start;
  }

  span {
    font-size: 1rem;
    color: rgba(255, 0, 0, 1);
  }
`;

const FormLabel = styled.label`
  font-size: 1.25rem;
  text-transform: uppercase;
  text-align: right;
  max-width: 116px;
  width: 100%;

  span {
    color: rgba(255, 0, 0, 1);
  }

  @media (max-width: ${breakpoints.mobile}) {
    max-width: 100%;
    text-align: left;
    font-size: 1rem;
  }
`;

const FieldsStyle = css`
  font-size: 1.25rem;
  border: none;
  border-radius: 6px;
  max-width: 627px;
  width: 100%;
  padding: 20px 10px;

  @media (max-width: ${breakpoints.mobile}) {
    max-width: 100%;
    padding: 10px;
    font-size: 1rem;
  }
`;

const FormInput = styled.input`
  ${FieldsStyle}
`;

const FotmTextArea = styled.textarea`
  ${FieldsStyle}
`;

const SubmitButton = styled.input`
  font-size: 1.5rem;
  border-radius: 6px;
  border: 4px solid rgba(255, 255, 255, 1);
  background: rgba(3, 14, 31, 1);
  max-width: 152px;
  width: 100%;
  color: rgba(255, 255, 255, 1);
  text-transform: uppercase;
  padding: 14px 15px;
  cursor: pointer;

  @media (max-width: ${breakpoints.mobile}) {
    padding: 10px;
    font-size: 1rem;
    border: 2px solid rgba(255, 255, 255, 1);
  }
`;

const FormStatus = styled.p`
  width: 100%;
  text-align: center;
  margin: 0;
  font-size: 1rem;
  color: ${({ $isError }) =>
    $isError ? 'rgba(255, 95, 95, 1)' : 'rgba(140, 255, 169, 1)'};
`;

const pushGtmEvent = (event, valueForm) => {
  if (typeof window === 'undefined' || !event) {
    return;
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event, formType: valueForm });
};

const ContactForm = ({ valueForm, gtmEvent }) => {
  const runtimeConfig = getRuntimeConfig();
  const recaptchaSiteKey = runtimeConfig.RECAPTCHA_SITE_KEY || '';
  const formEndpoint =
    runtimeConfig.CONTACT_FORM_ENDPOINT || DEFAULT_FORM_ENDPOINT;

  useEffect(() => {
    if (!recaptchaSiteKey) {
      return;
    }

    loadRecaptchaScript(recaptchaSiteKey).catch(() => {
      // The submit flow handles detailed reCAPTCHA errors.
    });
  }, [recaptchaSiteKey]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    project: '',
    tell: '',
    typeForm: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formStatus, setFormStatus] = useState({
    isError: false,
    message: '',
  });

  const onSubmit = async (data) => {
    try {
      setIsSubmitting(true);
      setFormStatus({ isError: false, message: '' });

      const recaptchaToken = await getRecaptchaToken(recaptchaSiteKey);

      await axios.post(
        formEndpoint,
        {
          ...data,
          recaptchaToken,
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      setFormData(data);
      setFormStatus({
        isError: false,
        message: 'Formulario enviado correctamente.',
      });
      pushGtmEvent(gtmEvent, valueForm);
      reset();
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'No se pudo enviar el formulario. Intenta de nuevo.';

      setFormStatus({ isError: true, message: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <FormContainer>
        <FormContent className="forms" onSubmit={handleSubmit(onSubmit)}>
          <FormRow>
            <FormLabel htmlFor="name">
              Nombre<span>*</span>
            </FormLabel>
            <FormInput
              {...register('name', { required: 'Campo requerido' })}
              type="text"
              id="name"
              name="name"
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
            />
            {errors.name && (
              <span className="error">{errors.name.message}</span>
            )}
          </FormRow>
          <FormRow>
            <FormLabel htmlFor="email">
              Email<span>*</span>
            </FormLabel>
            <FormInput
              {...register('email', { required: 'Campo requerido' })}
              type="email"
              id="email"
              name="email"
              onChange={(e) =>
                setFormData({ ...formData, email: e.target.value })
              }
            />
            {errors.email && (
              <span className="error">{errors.email.message}</span>
            )}
          </FormRow>
          <FormRow>
            <FormLabel htmlFor="project">
              Proyecto<span>*</span>
            </FormLabel>
            <FormInput
              {...register('project', { required: 'Campo requerido' })}
              type="text"
              id="project"
              name="project"
              onChange={(e) =>
                setFormData({ ...formData, project: e.target.value })
              }
            />

            {errors.project && (
              <span className="error">{errors.project.message}</span>
            )}
          </FormRow>
          <FormRow>
            <FormLabel htmlFor="tell">
              CUÉNTANOS SOBRE TU PROYECTO*<span>*</span>
            </FormLabel>
            <FotmTextArea
              {...register('tell', { required: 'Campo requerido' })}
              rows={6}
              type="textarea"
              id="tell"
              name="tell"
              onChange={(e) =>
                setFormData({ ...formData, tell: e.target.value })
              }
            />
            {errors.tell && (
              <span className="error">{errors.tell.message}</span>
            )}
          </FormRow>
          <FormRow>
            <FormInput
              {...register('typeForm')}
              onChange={(e) =>
                setFormData({ ...formData, typeForm: e.target.value })
              }
              type="hidden"
              id="typeForm"
              name="typeForm"
              value={valueForm}
            />
          </FormRow>
          <SubmitButton
            type="submit"
            value={isSubmitting ? 'Enviando...' : 'Enviar'}
            disabled={isSubmitting}
          />
          {formStatus.message && (
            <FormStatus $isError={formStatus.isError}>
              {formStatus.message}
            </FormStatus>
          )}
        </FormContent>
      </FormContainer>
    </>
  );
};

export default ContactForm;
